import React, { useState, useEffect } from 'react';
import { emailInboxAPI } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { MailSearch, Loader2, CheckCircle2, Plug, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export const EmailInboxSettings = () => {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email_address: '',
    app_password: '',
    imap_host: 'imap.gmail.com',
    imap_port: 993,
  });

  useEffect(() => {
    emailInboxAPI.getAccount()
      .then((res) => setAccount(res.data || null))
      .catch(() => setAccount(null))
      .finally(() => setLoading(false));
  }, []);

  const handleConnect = async (e) => {
    e.preventDefault();
    if (!form.email_address || !form.app_password) {
      toast.error('Email and app password are required');
      return;
    }
    setSaving(true);
    try {
      const res = await emailInboxAPI.connect(form);
      setAccount(res.data);
      toast.success('Inbox connected — you can now scan for POs');
      setForm({ ...form, app_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not connect inbox');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect this inbox?')) return;
    try {
      await emailInboxAPI.disconnect();
      setAccount(null);
      toast.success('Inbox disconnected');
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  return (
    <Card className="surface" data-testid="email-inbox-settings">
      <CardHeader className="border-b border-border">
        <CardTitle className="flex items-center gap-2">
          <MailSearch className="h-5 w-5 text-primary" />
          Email Inbox
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full brand-gradient text-white flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> AI
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : account ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25">
              <CheckCircle2 className="h-6 w-6 text-emerald-400" />
              <div className="flex-1">
                <p className="font-medium text-sm">Connected</p>
                <p className="text-xs text-muted-foreground">{account.email_address} · {account.imap_host}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleDisconnect} data-testid="disconnect-inbox-btn">Disconnect</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Go to <span className="text-foreground font-medium">PO Inbox</span> and hit <span className="text-foreground font-medium">Scan Inbox</span> to import new purchase orders.
            </p>
          </div>
        ) : (
          <form onSubmit={handleConnect} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Connect your email so AI can scan incoming purchase orders. For Gmail, create an
              {' '}<a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-primary hover:underline">App Password</a>{' '}
              (2-Step Verification must be on) and paste it below — not your normal password.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email address</Label>
                <Input type="email" value={form.email_address} onChange={(e) => setForm({ ...form, email_address: e.target.value })} placeholder="you@gmail.com" className="bg-input border-border" data-testid="inbox-email" />
              </div>
              <div className="space-y-2">
                <Label>App password</Label>
                <Input type="password" value={form.app_password} onChange={(e) => setForm({ ...form, app_password: e.target.value })} placeholder="xxxx xxxx xxxx xxxx" className="bg-input border-border" data-testid="inbox-app-password" />
              </div>
              <div className="space-y-2">
                <Label>IMAP host</Label>
                <Input value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} className="bg-input border-border" />
              </div>
              <div className="space-y-2">
                <Label>IMAP port</Label>
                <Input type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value, 10) || 993 })} className="bg-input border-border" />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="connect-inbox-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connecting…</> : <><Plug className="h-4 w-4 mr-2" /> Connect Inbox</>}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailInboxSettings;
